import ModalProvider from "@repo/components/src/globalProviders/modal";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { Heading } from "@repo/components/src/ui/heading";
import { Icon } from "@repo/components/src/ui/icon";
import {
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
} from "@repo/components/src/ui/modal";
import { Text } from "@repo/components/src/ui/text";
import { X } from "lucide-react-native";
import React, { ComponentProps, useCallback } from "react";

export type BasicModalProps = {
  title?: string | React.ReactNode;
  children?: React.ReactNode;
  cancelButton?: {
    text: string;
    props?: ComponentProps<typeof Button>;
  };
  footer?: React.ReactNode;
  showClose?: boolean;
  hide?: () => void;
  modalBodyProps?: Omit<ComponentProps<typeof ModalBody>, "children">;
  modalFooterProps?: Omit<ComponentProps<typeof ModalFooter>, "children">;
};

export default function BasicModal(props: BasicModalProps) {
  const {
    title,
    children,
    cancelButton,
    footer,
    showClose = true,
    hide,
    modalFooterProps,
  } = props;
  const hideFunction = useCallback(() => {
    hide ? hide() : ModalProvider.hide();
  }, [hide]);
  return (
    <>
      <ModalHeader>
        {typeof title === "string" ? (
          <Heading size="md" className="text-typography-950">
            {title}
          </Heading>
        ) : (
          title
        )}
        {showClose && (
          <ModalCloseButton
            onPress={hideFunction}
            className="absolute top-2 right-2 z-10"
          >
            <Icon
              as={X}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        )}
      </ModalHeader>
      <ModalBody
        showsVerticalScrollIndicator={false}
        scrollEnabled={true}
        {...props.modalBodyProps}
      >
        {React.Children.map(children, (ch, index) =>
          typeof ch === "string" ? (
            <Text
              size="sm"
              style={{
                marginBottom:
                  index + 1 === React.Children.count(children) ? 0 : 8,
              }}
            >
              {ch}
            </Text>
          ) : (
            ch
          ),
        )}
      </ModalBody>
      <ModalFooter className="w-full" {...modalFooterProps}>
        {!!cancelButton && (
          <Button
            variant="outline"
            action="secondary"
            onPress={ModalProvider.hide}
            {...cancelButton.props}
          >
            <ButtonText>{cancelButton.text}</ButtonText>
          </Button>
        )}
        {footer}
      </ModalFooter>
    </>
  );
}
